import type { PackageSelectorCatalog } from "@/lib/catalog-offer-seed";
/**
 * Stable catalog-taxonomy helpers shared by both package selectors.
 *
 * The backend currently stores Turkish and English display names only, while
 * its slugs are stable across all six storefront locales. UI components must
 * therefore localise known taxonomy from the slug instead of silently showing
 * the English database label on de/es/fr/pt pages.
 */
export const SERVICE_SLUGS = [
    "followers", "likes", "views", "comments", "subscribers", "retweets", "watch-time",
] as const;

export type CatalogServiceSlug = (typeof SERVICE_SLUGS)[number];

const SERVICE_SET = new Set<string>(SERVICE_SLUGS);

export function normaliseServiceSlug(slug: string): string {
    return slug === "watchtime" ? "watch-time" : slug;
}

/** Returns the canonical service represented by a direct or global-* slug. */
export function catalogServiceSlug(slug: string): CatalogServiceSlug | null {
    const candidate = normaliseServiceSlug(
        slug.startsWith("global-") ? slug.slice("global-".length) : slug,
    );
    return SERVICE_SET.has(candidate) ? candidate as CatalogServiceSlug : null;
}

export function isGlobalCatalogCategory(slug: string): boolean {
    return slug.startsWith("global-") && catalogServiceSlug(slug) !== null;
}

export function isLocalProfilesCategory(slug: string): boolean {
    return slug.startsWith("gercek-");
}

/**
 * Resolve a public service route (for example `followers`) to the matching
 * production catalogue category. The API uses `global-followers` for the
 * general storefront offer and may place market-specific `gercek-*` categories
 * first, so falling back to `categories[0]` silently shows the wrong product
 * family on `/buy/.../followers` pages.
 *
 * Prefer a genuine exact slug when one exists, then the global category for the
 * requested service, then any other category that canonically represents it.
 * The final first-item fallback preserves the existing behaviour only when the
 * route has no catalogue equivalent at all.
 */
export function selectCatalogCategoryForService<T extends { slug: string }>(
    categories: T[],
    requestedService?: string,
    sellableCategorySlugs?: Iterable<string>,
): T | undefined {
    if (categories.length === 0) return undefined;
    if (!requestedService) return categories[0];

    const requested = normaliseServiceSlug(requestedService);
    const representing = (pool: T[]) => pool.find((category) => normaliseServiceSlug(category.slug) === requested)
        ?? pool.find((category) => (
            isGlobalCatalogCategory(category.slug)
            && catalogServiceSlug(category.slug) === requested
        ))
        ?? pool.find((category) => catalogServiceSlug(category.slug) === requested);

    // BUG-CAT-02 (2026-09-23): the preference above is about IDENTITY, not
    // stock. When every global Instagram views package was paused, the route
    // still opened on the empty "Standard Views" tier and told the visitor there
    // was nothing to buy, while the real-profile list for the same service had
    // 7-8 packages. Given the categories that actually hold sellable packages for
    // this route (the page's own offer set), prefer those: the same identity
    // order first, then the market's real-profile list, which sells the route's
    // service through its sub-category. Without the set, behaviour is unchanged.
    const sellable = sellableCategorySlugs ? new Set(sellableCategorySlugs) : null;
    if (sellable && sellable.size > 0) {
        const stocked = categories.filter((category) => sellable.has(category.slug));
        const hit = representing(stocked) ?? stocked.find((category) => isLocalProfilesCategory(category.slug));
        if (hit) return hit;
    }
    return representing(categories) ?? categories[0];
}

/** The minimum an offer row must carry to steer the default selection. */
export interface CatalogDefaultOffer {
    category_slug: string;
    sub_category_slug?: string;
    charge_price?: number;
}

export interface SellableCatalogDefault {
    /** Category tab the route should open on (e.g. `global-views` or `gercek-en`). */
    categorySlug: string;
    /** Sub-category inside it: the service list of a real-profile category, or the entry tier of a global one. */
    subCategorySlug: string | null;
    /** Every category that holds at least one sellable offer for this route. */
    sellableCategorySlugs: string[];
}

/**
 * Where a service route's package selector should open, derived only from the
 * route's own sellable offer set (the array that also feeds its OfferCatalog).
 *
 * - The category follows selectCatalogCategoryForService restricted to stocked
 *   categories: the global category for the service when it sells anything,
 *   otherwise the real-profile (`gercek-*`) list that does.
 * - Inside a real-profile category the sub-category IS the service (followers,
 *   likes, views...), so the route's own service is chosen — never the list's
 *   first entry, which would be a different product.
 * - Inside a global category the first stocked tier in admin order is chosen, the
 *   same tier the server-rendered selector seed opens on.
 *
 * Null when there are no offers: an unavailable route has no default to open on.
 */
export function sellableCatalogDefault(
    offers: readonly CatalogDefaultOffer[] | undefined,
    requestedService: string | undefined,
    catalog?: PackageSelectorCatalog,
): SellableCatalogDefault | null {
    if (!offers || offers.length === 0 || !requestedService) return null;
    const sellableCategorySlugs = [...new Set(offers.map((offer) => offer.category_slug))];
    const category = selectCatalogCategoryForService(
        sellableCategorySlugs.map((slug) => ({ slug })),
        requestedService,
        sellableCategorySlugs,
    );
    if (!category) return null;

    const inCategory = offers.filter((offer) => offer.category_slug === category.slug);
    const requested = normaliseServiceSlug(requestedService);
    let subCategorySlug: string | null = null;
    if (isLocalProfilesCategory(category.slug)) {
        subCategorySlug = inCategory.find((offer) => (
            offer.sub_category_slug !== undefined
            && catalogServiceSlug(offer.sub_category_slug) === requested
        ))?.sub_category_slug ?? inCategory[0]?.sub_category_slug ?? null;
    } else {
        const adminSubs = catalog?.categories.find((item) => item.id === category.slug)?.subCategories;
        const firstStocked = adminSubs?.find((sub) => inCategory.some((offer) => offer.sub_category_slug === sub.slug));
        const entry = (firstStocked ? inCategory.find((offer) => offer.sub_category_slug === firstStocked.slug) : undefined) ?? inCategory[0];
        subCategorySlug = entry?.sub_category_slug ?? null;
    }
    return { categorySlug: category.slug, subCategorySlug, sellableCategorySlugs };
}

/**
 * Category slug → the sub-category slugs of that category that hold at least
 * one sellable offer of a route (the page's own offer set). A category whose
 * offers carry no sub-category is listed with an empty array ("stocked, tier
 * unknown").
 *
 * V-4 (A45 verification of BUG-CAT-01/02, 2026-09-23): the lower "all packages"
 * grid discovers its tabs through the /api cascade and opened on the API's
 * first tier — on /en/buy/instagram/views that was the paused Global Views →
 * Standard tier ("no packages") while the route sold 7 real-profile views
 * packages. The page hands this map to the grid so it can open on a tier that
 * actually sells. Plain JSON (sorted), so it crosses the server→client prop
 * boundary as-is.
 */
export type StockedCatalogTiers = Record<string, string[]>;

export function stockedCatalogTiers(
    offers: readonly CatalogDefaultOffer[] | undefined,
): StockedCatalogTiers {
    const tiers = new Map<string, Set<string>>();
    for (const offer of offers ?? []) {
        const subs = tiers.get(offer.category_slug) ?? new Set<string>();
        if (offer.sub_category_slug) subs.add(offer.sub_category_slug);
        tiers.set(offer.category_slug, subs);
    }
    return Object.fromEntries(
        [...tiers.keys()].sort().map((category) => [category, [...tiers.get(category)!].sort()]),
    );
}

/**
 * The sub-category a catalogue grid opens on inside `categorySlug`.
 *
 * - Inside a real-profile (`gercek-*`) list the sub-category IS the service
 *   (followers, likes, views…), so the requested route's service is chosen —
 *   never the list's first entry, which is a different product.
 * - Otherwise the catalogue's own order is kept (the API's sort_order, NOT a
 *   price order — which tier a page opens on is an owner decision), skipping
 *   tiers that hold no sellable offer for this route.
 * - Without stock information for this category the behaviour is the old one
 *   (the first sub-category), except for the real-profile service match.
 */
export function selectStockedSubCategory<T extends { slug?: string }>(
    subs: readonly T[],
    categorySlug: string,
    requestedService: string | undefined,
    stocked: StockedCatalogTiers | undefined,
): T | undefined {
    if (subs.length === 0) return undefined;
    const stockedSubs = stocked?.[categorySlug];
    const known = stockedSubs && stockedSubs.length > 0 ? new Set(stockedSubs) : null;
    const sells = (sub: T) => known === null || (sub.slug !== undefined && known.has(sub.slug));
    if (requestedService && isLocalProfilesCategory(categorySlug)) {
        const requested = normaliseServiceSlug(requestedService);
        const own = subs.find((sub) => (
            sub.slug !== undefined && catalogServiceSlug(sub.slug) === requested && sells(sub)
        ));
        if (own) return own;
    }
    return subs.find(sells) ?? subs[0];
}

/**
 * The `service` a checkout link carries for a package in (category, sub-category).
 *
 * Checkout decides the target link type (post URL for likes/views/comments,
 * profile URL otherwise) from `service`. A real-profile list slug (`gercek-en`)
 * names no service — its sub-category does — so a views package bought from it
 * asked for a PROFILE link. Same rule as the hero selector's
 * `checkoutServiceSlug` (components/UniversalPackageSelector.tsx).
 */
export function checkoutServiceSlug(categorySlug: string, subCategorySlug?: string | null): string {
    if (isLocalProfilesCategory(categorySlug) && subCategorySlug) {
        return catalogServiceSlug(subCategorySlug) ?? categorySlug;
    }
    return categorySlug;
}
